import React, { useState, useEffect } from 'react';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import Store from 'store/store';
import api from 'api';

import {
  makeStyles,
  CircularProgress,
  Table,
  TableRow,
  TableHead
} from '@material-ui/core';
import { CachedDataType } from 'store/modules/InvestmentAnalyser/types';
import TableComponents from 'components/Table';
import CircularProgressBar from 'components/Base/CircularProgressBar/CircularProgressBar';
import {
  getCachableData,
  getComfortRanges,
  getRiskRatingByTerm
} from './utils';
import { useTheme } from '@material-ui/styles';
import Charts from 'components/Charts';

const { selectors, actions } = Store;

const useStyles = makeStyles({
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '25rem'
  },
  tableHead: {
    '& th:not(:first-child)': {
      textAlign: 'center'
    }
  },
  riskScoreWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

interface RiskScroreProps {
  investments: any;
  riskRatings: CachedDataType;
  riskRatingsCacheIsValid: boolean;
  updateRiskRatings: Function;
  selectedInvestment: number;
  riskProfiler: string;
  provider?: any;
  range?: any;
}

const RiskScrore = ({
  investments,
  riskRatings,
  riskRatingsCacheIsValid,
  updateRiskRatings,
  selectedInvestment,
  riskProfiler,
  provider = null,
  range = null
}: RiskScroreProps) => {
  const theme = useTheme() as any;
  const classes = useStyles();
  const [data, setData] = useState(riskRatings.data as any);
  const [isLoading, setIsLoading] = useState(!riskRatings.investments.length);
  const [comfortRanges, setComfortRanges] = useState([] as any);

  useEffect(() => {
    if (!selectedInvestment && !provider) return;
    getCachableData(
      investments,
      riskRatingsCacheIsValid,
      setIsLoading,
      selectedInvestment
        ? () => api.getResearchItemRiskRatings(selectedInvestment)
        : () => api.getMpsHubRiskRatings(provider, range),
      updateRiskRatings,
      setData
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investments]);

  useEffect(() => {
    setData(riskRatings.data);
  }, [riskRatings]);

  useEffect(() => {
    if (riskProfiler === 'EValue') {
      return;
    }

    setComfortRanges(getComfortRanges(investments, data));
  }, [riskProfiler, data, investments]);

  return (
    <>
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <CircularProgress size="80px" />
        </div>
      ) : (
        <div style={{ paddingTop: '2rem' }}>
          {riskProfiler === 'EValue' ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableComponents.TableCell></TableComponents.TableCell>
                  <TableComponents.TableCell colSpan={5}>
                    Investment time horizon (years)
                  </TableComponents.TableCell>
                </TableRow>
              </TableHead>
              <TableComponents.TableHead
                cells={['Name', '3 - 5', '6 - 10', '11 - 15', '16 - 20', '21+']}
                className={classes.tableHead}
              />
              <TableComponents.TableBody>
                {investments.map((fund: any, index: number) => {
                  const prefix = fund.type === 'f' ? 'f' : 'mp';
                  const ratings = data[`${prefix}-${fund.id}`] || {
                    riskGroup: 10,
                    risks: []
                  };

                  const riskRatings = [
                    getRiskRatingByTerm(ratings, 4),
                    getRiskRatingByTerm(ratings, 8),
                    getRiskRatingByTerm(ratings, 13),
                    getRiskRatingByTerm(ratings, 18),
                    getRiskRatingByTerm(ratings, 25)
                  ];

                  return (
                    <TableRow key={index}>
                      <TableComponents.TableCell>
                        {fund.name}
                      </TableComponents.TableCell>
                      {riskRatings.map((riskRating, index) => {
                        return (
                          <TableComponents.TableCell key={index}>
                            {riskRating ? (
                              <div className={classes.riskScoreWrapper}>
                                <CircularProgressBar
                                  value={(riskRating / ratings.riskGroup) * 100}
                                  display={riskRating.toString()}
                                  color={
                                    theme.palette.baseColors.charts[
                                      'asset-allocation'
                                    ][riskRating - 1]
                                  }
                                />
                              </div>
                            ) : (
                              '-'
                            )}
                          </TableComponents.TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableComponents.TableBody>
            </Table>
          ) : (
            <Charts.MorningstarRiskProfiler.ComfortRanges
              data={comfortRanges}
            />
          )}
        </div>
      )}
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.checkedResearchInvestments(state),
      riskRatings: selectors.investmentAnalyserSelectors.riskRatings(state),
      pastPerformance:
        selectors.investmentAnalyserSelectors.pastPerformance(state),
      riskRatingsCacheIsValid:
        selectors.investmentAnalyserSelectors.riskRatingsCacheIsValid(state),
      riskProfiler: selectors.firmSelectors.riskProfiler(state)
    }),
    {
      updateRiskRatings: actions.investmentAnalyserActions.updateRiskRatings
    }
  )
)(RiskScrore);
